import type { CommunicationStatus } from "@/type/api";
import { COMMUNICATION_STATUS_BUCKETS } from "@/type/api";

/**
 * The communication list's status checkboxes. A key is either one status's own
 * checkbox (`new`, `opened`, `pending`) or a whole bucket's (`closed`), so
 * `closed` alone stands for the five terminal statuses: `closed`, `resolved`,
 * `possible-issue`, `possible-resolved` and `invalid`.
 */
export interface CommunicationStatusFilter {
	new: boolean;
	opened: boolean;
	pending: boolean;
	closed: boolean;
}

/**
 * The row the workbench opens with: the server's open bucket, which is what
 * `GET /api/communication` returns by default. `new`, `opened` and `pending`
 * are ticked because they are that bucket; `closed` is not, so no terminal
 * status is in the default view.
 */
export const DEFAULT_COMMUNICATION_STATUS_FILTER: CommunicationStatusFilter = {
	new: true,
	opened: true,
	pending: true,
	closed: false,
};

/** The statuses the ticked boxes admit, expanded through the bucket map:
 * `new`, `opened` and `pending` stand for themselves, `closed` for its whole
 * bucket. Nothing ticked admits nothing. */
export function selectedStatuses(
	filter: CommunicationStatusFilter,
): ReadonlySet<CommunicationStatus> {
	const selected = new Set<CommunicationStatus>();
	for (const status of COMMUNICATION_STATUS_BUCKETS.open) {
		// The open bucket's statuses are the per-status checkboxes' names, so a
		// status's own box is what governs it.
		if (filter[status as keyof CommunicationStatusFilter]) {
			selected.add(status);
		}
	}
	if (filter.closed) {
		for (const status of COMMUNICATION_STATUS_BUCKETS.closed) {
			selected.add(status);
		}
	}
	return selected;
}

/** Whether a communication survives the status row: its status is one of the
 * statuses the ticked boxes name, and a status the vocabulary does not know is
 * not among them. */
export function communicationMatchesStatus(
	comm: { status: string },
	filter: CommunicationStatusFilter,
): boolean {
	return selectedStatuses(filter).has(comm.status as CommunicationStatus);
}

/** Whether the row is untouched. A non-default row is a filter the operator
 * applied, so the toolbar reports it rather than passing it over as the view's
 * natural state. */
export function statusFilterIsDefault(
	filter: CommunicationStatusFilter,
): boolean {
	return (
		filter.new === DEFAULT_COMMUNICATION_STATUS_FILTER.new &&
		filter.opened === DEFAULT_COMMUNICATION_STATUS_FILTER.opened &&
		filter.pending === DEFAULT_COMMUNICATION_STATUS_FILTER.pending &&
		filter.closed === DEFAULT_COMMUNICATION_STATUS_FILTER.closed
	);
}
